import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Chip,
  Checkbox,
  Tooltip,
  Pagination,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
} from '@mui/material';
import { Edit as EditIcon, Close as CloseIcon, ArrowBack as ArrowBackIcon, Add as AddIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, People as PeopleIcon } from '@mui/icons-material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import PageLayout from '../components/common/PageLayout';
import { get, post, patch, put } from '../utils/api';
/** Unique sponsor display names; multiple pledge links same person → one row, pledgeCount = link count */
function consolidateUniqueSponsors(row) {
  const byKey = new Map();
  for (const s of row?.sponsors || []) {
    const raw = String(s.sponsor_name || '').trim() || 'Unknown';
    const key = raw.toLowerCase();
    if (!byKey.has(key)) {
      byKey.set(key, { displayName: raw, contact_id: s.contact_id || null, pledgeCount: 0, pledgeIds: new Set() });
    }
    const e = byKey.get(key);
    e.pledgeCount += 1;
    if (s.pledge_id) e.pledgeIds.add(String(s.pledge_id));
    if (!e.contact_id && s.contact_id) e.contact_id = s.contact_id;
  }
  return Array.from(byKey.values())
    .map(({ displayName, contact_id, pledgeCount, pledgeIds }) => ({
      displayName,
      contact_id,
      pledgeCount,
      orderCount: pledgeIds.size || pledgeCount,
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export default function KotilingaInventoryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = React.useState([]);
  const [categories, setCategories] = React.useState([]);
  const [statuses, setStatuses] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [statusFilter, setStatusFilter] = React.useState('all');
  const [searchQ, setSearchQ] = React.useState('');
  const [debouncedQ, setDebouncedQ] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(25);
  const [total, setTotal] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [sponsorSummary, setSponsorSummary] = React.useState([]);

  const [editOpen, setEditOpen] = React.useState(false);
  const [editingItem, setEditingItem] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState('');
  const [saveSuccess, setSaveSuccess] = React.useState('');
  const [editProductInput, setEditProductInput] = React.useState('');
  const [editProductOptions, setEditProductOptions] = React.useState([]);
  const [editProductLoading, setEditProductLoading] = React.useState(false);
  const [selectedEditProduct, setSelectedEditProduct] = React.useState(null);
  const [editVariationOptions, setEditVariationOptions] = React.useState([]);
  const [editVariationLoading, setEditVariationLoading] = React.useState(false);
  const [editUnlinkedLoading, setEditUnlinkedLoading] = React.useState(false);
  const [editUnlinkedError, setEditUnlinkedError] = React.useState('');
  const [editUnlinkedRows, setEditUnlinkedRows] = React.useState([]);
  const [editUnlinkedSelectedIds, setEditUnlinkedSelectedIds] = React.useState([]);
  const [addSponsorOpen, setAddSponsorOpen] = React.useState(false);
  const [addSponsorItem, setAddSponsorItem] = React.useState(null);
  const [addingSponsor, setAddingSponsor] = React.useState(false);
  const [addSponsorError, setAddSponsorError] = React.useState('');
  const [addSponsorEngraveName, setAddSponsorEngraveName] = React.useState('');
  const [addSponsorManualName, setAddSponsorManualName] = React.useState('');
  const [unlinkingSponsorKey, setUnlinkingSponsorKey] = React.useState(''); // 'inventoryId-index' while unlinking
  /** Row whose consolidated sponsors are shown in dialog (inventory-focused table hides names in grid) */
  const [sponsorsDialogRow, setSponsorsDialogRow] = React.useState(null);
  const [devoteeInput, setDevoteeInput] = React.useState('');
  const [devoteeOptions, setDevoteeOptions] = React.useState([]);
  const [devoteeLoading, setDevoteeLoading] = React.useState(false);
  const [selectedDevotee, setSelectedDevotee] = React.useState(null);
  const [pledges, setPledges] = React.useState([]);
  const [pledgeLoading, setPledgeLoading] = React.useState(false);
  const [selectedPledge, setSelectedPledge] = React.useState(null);
  const [bulkAddOpen, setBulkAddOpen] = React.useState(false);
  const [bulkAddItem, setBulkAddItem] = React.useState(null);
  const [bulkPledges, setBulkPledges] = React.useState([]);
  const [bulkPledgesLoading, setBulkPledgesLoading] = React.useState(false);
  const [bulkPledgesError, setBulkPledgesError] = React.useState('');
  const [bulkSelectedPledgeIds, setBulkSelectedPledgeIds] = React.useState([]);
  const [bulkSaving, setBulkSaving] = React.useState(false);
  const [paymentsOpen, setPaymentsOpen] = React.useState(false);
  const [paymentsLoading, setPaymentsLoading] = React.useState(false);
  const [paymentsError, setPaymentsError] = React.useState('');
  const [paymentOrders, setPaymentOrders] = React.useState([]);
  const [paymentContext, setPaymentContext] = React.useState(null);
  const [syncOpen, setSyncOpen] = React.useState(false);
  const [syncLoading, setSyncLoading] = React.useState(false);
  const [syncSaving, setSyncSaving] = React.useState(false);
  const [syncError, setSyncError] = React.useState('');
  const [syncGroups, setSyncGroups] = React.useState([]);
  const [syncMeta, setSyncMeta] = React.useState({ items_with_new: 0, items_with_voided: 0, new_pledges_total: 0, voided_total: 0 });
  const [syncSelectedAdds, setSyncSelectedAdds] = React.useState({});
  const [syncSelectedRemoves, setSyncSelectedRemoves] = React.useState({});
  const [addItemOpen, setAddItemOpen] = React.useState(false);
  const [addItemSaving, setAddItemSaving] = React.useState(false);
  const [addItemError, setAddItemError] = React.useState('');
  const [addItemSuccess, setAddItemSuccess] = React.useState('');
  const [addQuantity, setAddQuantity] = React.useState('0');
  const [addProductInput, setAddProductInput] = React.useState('');
  const [addProductOptions, setAddProductOptions] = React.useState([]);
  const [addProductLoading, setAddProductLoading] = React.useState(false);
  const [selectedAddProduct, setSelectedAddProduct] = React.useState(null);
  const [addVariationOptions, setAddVariationOptions] = React.useState([]);
  const [addVariationLoading, setAddVariationLoading] = React.useState(false);
  const [selectedAddVariation, setSelectedAddVariation] = React.useState(null);
  const [addCategoryId, setAddCategoryId] = React.useState('');
  const [missingItemsOpen, setMissingItemsOpen] = React.useState(false);
  const [missingProducts, setMissingProducts] = React.useState([]);
  const [missingProductsLoading, setMissingProductsLoading] = React.useState(false);
  const [missingProductsError, setMissingProductsError] = React.useState('');
  const [issueSummaryByItem, setIssueSummaryByItem] = React.useState({});
  const [issuesOpen, setIssuesOpen] = React.useState(false);
  const [issuesLoading, setIssuesLoading] = React.useState(false);
  const [issuesSaving, setIssuesSaving] = React.useState(false);
  const [issuesError, setIssuesError] = React.useState('');
  const [issuesSuccess, setIssuesSuccess] = React.useState('');
  const [issuesItem, setIssuesItem] = React.useState(null);
  const [issuesData, setIssuesData] = React.useState(null);
  const [selectedMissingCustomerKeys, setSelectedMissingCustomerKeys] = React.useState([]);
  const [selectedUnlinkedPledgeIds, setSelectedUnlinkedPledgeIds] = React.useState([]);
  const [exceptionOpen, setExceptionOpen] = React.useState(false);
  const [exceptionLoading, setExceptionLoading] = React.useState(false);
  const [exceptionError, setExceptionError] = React.useState('');
  const [exceptionRows, setExceptionRows] = React.useState([]);
  const [exceptionMeta, setExceptionMeta] = React.useState({ items_with_alerts: 0, total_alerts: 0, missing_orders_total: 0, unlinked_pledges_total: 0 });
  const [exportingPdf, setExportingPdf] = React.useState(false);

  const formatCurrency = React.useCallback((value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);
  }, []);
  const formatSummaryCurrency = React.useCallback((value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  }, []);
  const productLabel = React.useCallback((product) => {
    if (!product) return '';
    const name = String(product.name || '').trim();
    const sku = String(product.sku || '').trim();
    if (name && sku) return `${name} · ${sku}`;
    return name || sku || '';
  }, []);


  const normalizeProductOptions = React.useCallback((list) => {
    const byId = new Map();
    for (const raw of Array.isArray(list) ? list : []) {
      const candidate = {
        id: Number(raw?.id),
        name: raw?.name || `Product #${raw?.id}`,
        sku: raw?.sku || '',
        price_num: raw?.price_num,
      };
      if (!Number.isFinite(candidate.id) || candidate.id <= 0) continue;
      const existing = byId.get(candidate.id);
      if (!existing) {
        byId.set(candidate.id, candidate);
        continue;
      }
      const existingName = String(existing.name || '').trim();
      const nextName = String(candidate.name || '').trim();
      const existingLooksExpanded = existingName.includes(' - ');
      const nextLooksExpanded = nextName.includes(' - ');
      if (existingLooksExpanded && !nextLooksExpanded) {
        byId.set(candidate.id, candidate);
        continue;
      }
      if (existingLooksExpanded === nextLooksExpanded && nextName.length < existingName.length) {
        byId.set(candidate.id, candidate);
      }
    }
    return Array.from(byId.values());
  }, []);
  React.useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(searchQ.trim().length >= 2 ? searchQ.trim() : '');
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [searchQ]);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await get('/ldb/kotilinga-inventory/categories');
        const list = Array.isArray(res?.data) ? res.data : [];
        list.sort((a, b) => String(a?.category_name || '').localeCompare(String(b?.category_name || ''), undefined, { sensitivity: 'base' }));
        setCategories(list);
      } catch (e) {
        console.error('Failed to load categories', e);
        setCategories([]);
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await get('/ldb/kotilinga-inventory/statuses');
        setStatuses(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load statuses', e);
        setStatuses([]);
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        const res = await get('/ldb/kotilinga-inventory/sponsor-summary-by-category');
        setSponsorSummary(Array.isArray(res?.data) ? res.data : []);
      } catch (e) {
        console.error('Failed to load sponsor summary', e);
        setSponsorSummary([]);
      }
    })();
  }, []);

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError('');
        const params = { page: page.toString(), per_page: perPage.toString() };
        if (categoryFilter !== 'all') params.category_id = categoryFilter;
        if (statusFilter !== 'all') params.status = statusFilter;
        if (debouncedQ) params.q = debouncedQ;

        const res = await get('/ldb/kotilinga-inventory/items', params);
        setData(Array.isArray(res?.data) ? res.data : []);
        setTotal(res?.meta?.total ?? 0);
        setTotalPages(res?.meta?.total_pages ?? Math.ceil((res?.meta?.total ?? 0) / perPage));
      } catch (e) {
        setError(e?.message || 'Failed to load inventory');
      } finally {
        setLoading(false);
      }
    })();
  }, [page, perPage, categoryFilter, statusFilter, debouncedQ]);

  const variationLabel = React.useCallback((v) => {
    const direct = String(v?.display_name || v?.name || '').trim();
    const sku = String(v?.sku || '').trim();
    let attrs = v?.attributes;
    if (typeof attrs === 'string') {
      try { attrs = JSON.parse(attrs); } catch { attrs = null; }
    }
    if (Array.isArray(attrs)) {
      const options = attrs.map((a) => String(a?.option || '').trim()).filter(Boolean);
      const attrLabel = options.join(' · ');
      if (attrLabel && (!direct || direct === sku)) return attrLabel;
      if (attrLabel && direct && /^[-.\\w]+$/.test(direct) && /[A-Za-z]/.test(attrLabel)) return attrLabel;
    }
    return direct || sku || 'Variation';
  }, []);

  const loadEditVariations = React.useCallback(async (productId) => {
    if (!productId) {
      setEditVariationOptions([]);
      return;
    }
    setEditVariationLoading(true);
    try {
      const res = await get(`/ldb/products/${productId}`);
      const list = Array.isArray(res?.variations) ? res.variations : [];
      setEditVariationOptions(list);
    } catch {
      setEditVariationOptions([]);
    } finally {
      setEditVariationLoading(false);
    }
  }, []);

  const loadAddVariations = React.useCallback(async (productId, options = {}) => {
    if (!productId) {
      setAddVariationOptions([]);
      return;
    }
    setAddVariationLoading(true);
    try {
      const res = await get(`/ldb/products/${productId}`);
      const list = Array.isArray(res?.variations) ? res.variations : [];
      const excludedIds = new Set((options.excludeVariationIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0));
      const filteredList = excludedIds.size > 0
        ? list.filter((variation) => !excludedIds.has(Number(variation?.id || 0)))
        : list;
      setAddVariationOptions(filteredList);
    } catch {
      setAddVariationOptions([]);
    } finally {
      setAddVariationLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!editOpen) {
      setEditProductOptions([]);
      return;
    }
    const q = editProductInput.trim();
    if (q.length < 2) {
      setEditProductOptions(selectedEditProduct ? [selectedEditProduct] : []);
      return;
    }
    let cancelled = false;
    setEditProductLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await get('/ldb/products', { q, page: 1, per_page: 20 });
        const list = Array.isArray(res?.data) ? res.data : [];
        const mapped = normalizeProductOptions(list);
        const selected = selectedEditProduct;
        const merged = selected && !mapped.some((p) => p.id === selected.id) ? [selected, ...mapped] : mapped;
        if (!cancelled) setEditProductOptions(merged);
      } catch {
        if (!cancelled) setEditProductOptions(selectedEditProduct ? [selectedEditProduct] : []);
      } finally {
        if (!cancelled) setEditProductLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [editOpen, editProductInput, selectedEditProduct]);

  React.useEffect(() => {
    if (!addItemOpen) {
      setAddProductOptions([]);
      return;
    }
    const q = addProductInput.trim();
    if (q.length < 2) {
      setAddProductOptions(selectedAddProduct ? [selectedAddProduct] : []);
      return;
    }
    let cancelled = false;
    setAddProductLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await get('/ldb/products', { q, page: 1, per_page: 20 });
        const list = Array.isArray(res?.data) ? res.data : [];
        const mapped = normalizeProductOptions(list);
        const selected = selectedAddProduct;
        const merged = selected && !mapped.some((p) => p.id === selected.id) ? [selected, ...mapped] : mapped;
        if (!cancelled) setAddProductOptions(merged);
      } catch {
        if (!cancelled) setAddProductOptions(selectedAddProduct ? [selectedAddProduct] : []);
      } finally {
        if (!cancelled) setAddProductLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addItemOpen, addProductInput, selectedAddProduct]);

  const handleEdit = (item) => {
    const productId = item.product_id == null || item.product_id === '' ? '' : Number(item.product_id);
    const selectedProduct = productId
      ? { id: productId, name: item.product_name || `Product #${productId}`, sku: '' }
      : null;
    setEditingItem({
      inventory_id: item.inventory_id,
      product_name: item.product_name ?? '',
      product_id: productId,
      sponsors: Array.isArray(item.sponsors) ? item.sponsors : [],
      group_name: item.group_name ?? '',
      product_no: item.product_no ?? '',
      quantity: item.quantity ?? 0,
      variation_id: item.variation_id == null || item.variation_id === '' ? '' : Number(item.variation_id),
      pledge_amount: item.pledge_amount ?? '',
      category_id: item.category_id ?? '',
      status: item.status ?? '',
    });
    setSelectedEditProduct(selectedProduct);
    setEditProductInput(selectedProduct?.name || '');
    setEditProductOptions(selectedProduct ? [selectedProduct] : []);
    void loadEditVariations(productId || null);
    setEditOpen(true);
    setSaveError('');
    setSaveSuccess('');
    setEditUnlinkedError('');
    setEditUnlinkedRows([]);
    setEditUnlinkedSelectedIds([]);
  };

  const handleSave = async () => {
    if (!editingItem) return;
    try {
      setSaving(true);
      setSaveError('');
      const payload = {
        product_name: editingItem.product_name || null,
        product_id: editingItem.product_id === '' ? null : Number(editingItem.product_id),
        group_name: editingItem.group_name || null,
        product_no: editingItem.product_no || null,
        quantity: editingItem.quantity === '' ? null : Number(editingItem.quantity),
        variation_id: editingItem.variation_id === '' ? 0 : Number(editingItem.variation_id),
        pledge_amount: editingItem.pledge_amount === '' ? null : parseFloat(editingItem.pledge_amount),
        category_id: editingItem.category_id === '' ? null : Number(editingItem.category_id),
        status: editingItem.status === '' ? null : Number(editingItem.status),
      };
      await patch(`/ldb/kotilinga-inventory/items/${editingItem.inventory_id}`, payload);

      const params = { page: page.toString(), per_page: perPage.toString() };
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedQ) params.q = debouncedQ;
      const res = await get('/ldb/kotilinga-inventory/items', params);
      const list = Array.isArray(res?.data) ? res.data : [];
      setData(list);
      setTotal(res?.meta?.total ?? 0);
      setTotalPages(res?.meta?.total_pages ?? 0);
      const refreshed = list.find((r) => r.inventory_id === editingItem.inventory_id);
      setEditingItem((prev) => (prev && refreshed ? { ...prev, sponsors: refreshed.sponsors || prev.sponsors } : prev));
      setSaveSuccess('Item saved.');
      setTimeout(() => setSaveSuccess(''), 4000);
    } catch (e) {
      setSaveError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const refreshItems = React.useCallback(async () => {
    const params = { page: page.toString(), per_page: perPage.toString() };
    if (categoryFilter !== 'all') params.category_id = categoryFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (debouncedQ) params.q = debouncedQ;
    const res = await get('/ldb/kotilinga-inventory/items', params);
    setData(Array.isArray(res?.data) ? res.data : []);
    setTotal(res?.meta?.total ?? 0);
    setTotalPages(res?.meta?.total_pages ?? 0);
  }, [page, perPage, categoryFilter, statusFilter, debouncedQ]);

  React.useEffect(() => {
    const ids = (Array.isArray(data) ? data : [])
      .map((r) => Number(r?.inventory_id || 0))
      .filter((n) => Number.isInteger(n) && n > 0);
    if (ids.length === 0) {
      setIssueSummaryByItem({});
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await get('/ldb/kotilinga-inventory/issues-summary', {
          inventory_ids: ids.join(','),
        });
        if (cancelled) return;
        const list = Array.isArray(res?.data) ? res.data : [];
        const next = {};
        for (const r of list) {
          const key = String(r?.inventory_id || '');
          if (!key) continue;
          next[key] = {
            missing_orders_count: Number(r?.missing_orders_count || 0),
            unlinked_pledges_count: Number(r?.unlinked_pledges_count || 0),
            total_alerts: Number(r?.total_alerts || 0),
            has_alerts: Boolean(r?.has_alerts),
          };
        }
        setIssueSummaryByItem(next);
      } catch {
        if (!cancelled) setIssueSummaryByItem({});
      }
    })();
    return () => { cancelled = true; };
  }, [data]);

  const loadItemIssues = React.useCallback(async (inventoryId) => {
    const id = Number(inventoryId || 0);
    if (!id) return null;
    const res = await get(`/ldb/kotilinga-inventory/items/${id}/issues`);
    return res?.data || null;
  }, []);

  const openIssuesDialog = async (row, e) => {
    e?.stopPropagation?.();
    setIssuesOpen(true);
    setIssuesLoading(true);
    setIssuesSaving(false);
    setIssuesError('');
    setIssuesSuccess('');
    setIssuesItem(row || null);
    setIssuesData(null);
    setSelectedMissingCustomerKeys([]);
    setSelectedUnlinkedPledgeIds([]);
    try {
      const detail = await loadItemIssues(row?.inventory_id);
      setIssuesData(detail);
    } catch (err) {
      setIssuesError(err?.message || 'Failed to load findings');
    } finally {
      setIssuesLoading(false);
    }
  };

  const toggleMissingCustomer = (groupKey, checked) => {
    const key = String(groupKey);
    setSelectedMissingCustomerKeys((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((id) => id !== key);
    });
  };

  const toggleUnlinkedPledge = (pledgeId, checked) => {
    const key = String(pledgeId);
    setSelectedUnlinkedPledgeIds((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((id) => id !== key);
    });
  };

  const toggleAllMissingOrders = (checked) => {
    const ids = (issuesData?.missing_pledge_customers || []).map((r) => String(r.group_key));
    setSelectedMissingCustomerKeys(checked ? ids : []);
  };

  const toggleAllUnlinkedPledges = (checked) => {
    const ids = (issuesData?.unlinked_pledges || []).map((r) => String(r.pledge_id));
    setSelectedUnlinkedPledgeIds(checked ? ids : []);
  };

  const openOrderEditDialog = (orderId) => {
    const id = Number(orderId || 0);
    if (!id) return;
    navigate(`/orders/${id}/edit`, { state: { returnTo: `${location.pathname}${location.search}` } });
  };

  React.useEffect(() => {
    if (!location.state?.orderEditSaved) return;
    let cancelled = false;
    (async () => {
      await refreshItems();
      const iid = issuesData?.inventory_id;
      if (iid && !cancelled) {
        try {
          const d = await loadItemIssues(iid);
          if (d && !cancelled) setIssuesData(d);
        } catch { /* noop */ }
      }
      if (!cancelled) {
        navigate(`${location.pathname}${location.search}`, { replace: true, state: {} });
      }
    })();
    return () => { cancelled = true; };
  }, [location.state?.orderEditSaved]);

  const handleCreateMissingPledges = async () => {
    if (!issuesData?.inventory_id || selectedMissingCustomerKeys.length === 0) return;
    try {
      setIssuesSaving(true);
      setIssuesError('');
      setIssuesSuccess('');
      const selectedGroups = (issuesData?.missing_pledge_customers || [])
        .filter((g) => selectedMissingCustomerKeys.includes(String(g.group_key)));
      const orderIds = [...new Set(selectedGroups.flatMap((g) => Array.isArray(g.order_ids) ? g.order_ids.map((x) => Number(x)).filter((n) => Number.isInteger(n) && n > 0) : []))];
      const res = await post(`/ldb/kotilinga-inventory/items/${issuesData.inventory_id}/create-pledges`, {
        order_ids: orderIds,
      });
      const converted = Number(res?.meta?.created || (Array.isArray(res?.data?.created_pledges) ? res.data.created_pledges.length : 0));
      const failed = Number(res?.meta?.failed || (Array.isArray(res?.data?.failed) ? res.data.failed.length : 0));
      setIssuesSuccess(`Pledge creation completed. Created: ${converted}. Failed: ${failed}.`);
      setSelectedMissingCustomerKeys([]);
      await refreshItems();
      const refreshed = await loadItemIssues(issuesData.inventory_id);
      setIssuesData(refreshed);
    } catch (err) {
      setIssuesError(err?.message || 'Failed to create pledges');
    } finally {
      setIssuesSaving(false);
    }
  };

  const handleLinkUnlinkedPledges = async () => {
    if (!issuesData?.inventory_id || selectedUnlinkedPledgeIds.length === 0) return;
    try {
      setIssuesSaving(true);
      setIssuesError('');
      setIssuesSuccess('');
      const existing = (issuesData.current_sponsors || []).map((s) => ({
        sponsor_name: s?.sponsor_name ?? '',
        contact_id: s?.contact_id ?? '',
        pledge_id: s?.pledge_id ?? '',
        engrave_name: s?.engrave_name ?? '',
      }));
      const existingPledgeIds = new Set(
        existing.map((s) => String(s.pledge_id || '').trim()).filter((id) => /^\d+$/.test(id))
      );
      const additions = (issuesData.unlinked_pledges || [])
        .filter((p) => selectedUnlinkedPledgeIds.includes(String(p.pledge_id)))
        .filter((p) => !existingPledgeIds.has(String(p.pledge_id)))
        .map((p) => ({
          sponsor_name: p.sponsor_name || 'Unknown sponsor',
          contact_id: p.contact_id != null ? String(p.contact_id) : '',
          pledge_id: String(p.pledge_id),
          engrave_name: '',
        }));

      if (additions.length === 0) {
        setIssuesError('Selected pledges are already linked.');
        return;
      }
      await put(`/ldb/kotilinga-inventory/items/${issuesData.inventory_id}/sponsors`, {
        sponsors: [...existing, ...additions],
      });
      setIssuesSuccess(`Linked ${additions.length} pledge(s) to this inventory item.`);
      setSelectedUnlinkedPledgeIds([]);
      await refreshItems();
      const refreshed = await loadItemIssues(issuesData.inventory_id);
      setIssuesData(refreshed);
    } catch (err) {
      setIssuesError(err?.message || 'Failed to link pledges');
    } finally {
      setIssuesSaving(false);
    }
  };

  const openAddItemDialog = () => {
    setAddItemError('');
    setAddItemSuccess('');
    setSelectedAddProduct(null);
    setSelectedAddVariation(null);
    setAddProductInput('');
    setAddProductOptions([]);
    setAddVariationOptions([]);
    setAddCategoryId('');
    setAddQuantity('0');
    setAddItemOpen(true);
  };

  const openMissingItemsDialog = async () => {
    setMissingItemsOpen(true);
    setMissingProductsError('');
    setMissingProducts([]);
    setSelectedAddProduct(null);
    setSelectedAddVariation(null);
    setAddVariationOptions([]);
    setAddCategoryId('');
    setAddQuantity('0');
    setAddItemError('');
    setAddItemSuccess('');
    setMissingProductsLoading(true);
    try {
      const res = await get('/ldb/kotilinga-inventory/products-not-in-inventory');
      setMissingProducts(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setMissingProductsError(e?.message || 'Failed to load missing products');
    } finally {
      setMissingProductsLoading(false);
    }
  };

  const closeMissingItemsDialog = () => {
    if (addItemSaving) return;
    setMissingItemsOpen(false);
    setMissingProductsError('');
    setAddItemError('');
    setAddItemSuccess('');
    setSelectedAddProduct(null);
    setSelectedAddVariation(null);
    setAddVariationOptions([]);
  };


  const openSyncDialog = async () => {
    setSyncOpen(true);
    setSyncLoading(true);
    setSyncSaving(false);
    setSyncError('');
    setSyncGroups([]);
    setSyncSelectedAdds({});
    setSyncSelectedRemoves({});
    try {
      const res = await get('/ldb/kotilinga-inventory/pending-links');
      const groups = Array.isArray(res?.data) ? res.data : [];
      setSyncGroups(groups);
      setSyncMeta(res?.meta || { items_with_new: 0, items_with_voided: 0, new_pledges_total: 0, voided_total: 0 });
    } catch (e) {
      setSyncError(e?.message || 'Failed to load sponsor sync review');
    } finally {
      setSyncLoading(false);
    }
  };

  const openExceptionDialog = async () => {
    setExceptionOpen(true);
    setExceptionLoading(true);
    setExceptionError('');
    setExceptionRows([]);
    setExceptionMeta({ items_with_alerts: 0, total_alerts: 0, missing_orders_total: 0, unlinked_pledges_total: 0 });
    try {
      const res = await get('/ldb/kotilinga-inventory/exceptions');
      setExceptionRows(Array.isArray(res?.data) ? res.data : []);
      setExceptionMeta(res?.meta || { items_with_alerts: 0, total_alerts: 0, missing_orders_total: 0, unlinked_pledges_total: 0 });
    } catch (e) {
      setExceptionError(e?.message || 'Failed to load exception report');
    } finally {
      setExceptionLoading(false);
    }
  };

  const toggleSyncAdd = (inventoryId, pledgeId, checked) => {
    const key = String(inventoryId);
    const id = String(pledgeId);
    setSyncSelectedAdds((prev) => {
      const set = new Set(prev[key] || []);
      if (checked) set.add(id); else set.delete(id);
      return { ...prev, [key]: [...set] };
    });
  };

  const toggleSyncRemove = (inventoryId, sponsorRowId, checked) => {
    const key = String(inventoryId);
    const id = String(sponsorRowId);
    setSyncSelectedRemoves((prev) => {
      const set = new Set(prev[key] || []);
      if (checked) set.add(id); else set.delete(id);
      return { ...prev, [key]: [...set] };
    });
  };

  const applySyncSelections = async () => {
    try {
      setSyncSaving(true);
      setSyncError('');
      for (const g of syncGroups) {
        const invKey = String(g.inventory_id);
        const addIds = new Set(syncSelectedAdds[invKey] || []);
        const removeIds = new Set(syncSelectedRemoves[invKey] || []);
        if (addIds.size === 0 && removeIds.size === 0) continue;

        const current = (g.current_sponsors || []).filter((s) => !removeIds.has(String(s.id)));
        const existingPledgeIds = new Set(
          current.map((s) => String(s?.pledge_id || '').trim()).filter((id) => /^\d+$/.test(id))
        );
        const addRows = (g.new_pledges || []).filter((p) => addIds.has(String(p.pledge_id)));
        for (const p of addRows) {
          const pid = String(p.pledge_id);
          if (existingPledgeIds.has(pid)) continue;
          current.push({
            sponsor_name: p.sponsor_name || 'Unknown sponsor',
            contact_id: p.contact_id != null ? String(p.contact_id) : '',
            pledge_id: pid,
            engrave_name: '',
          });
        }
        const payload = current.map((s) => ({
          sponsor_name: s.sponsor_name ?? '',
          contact_id: s.contact_id ?? '',
          pledge_id: s.pledge_id ?? '',
          engrave_name: s.engrave_name ?? '',
        }));
        await put(`/ldb/kotilinga-inventory/items/${g.inventory_id}/sponsors`, { sponsors: payload });
      }
      await refreshItems();
      setSyncOpen(false);
    } catch (e) {
      setSyncError(e?.message || 'Failed to apply selected sync changes');
    } finally {
      setSyncSaving(false);
    }
  };

  const closeAddItemDialog = () => {
    if (addItemSaving) return;
    setAddItemOpen(false);
    setAddItemError('');
    setAddItemSuccess('');
  };

  const addItemNamePreview = React.useMemo(() => {
    const productName = String(selectedAddProduct?.name || '').trim();
    const varName = selectedAddVariation ? String(variationLabel(selectedAddVariation) || '').trim() : '';
    if (productName && varName) return `${productName} - ${varName}`;
    return productName;
  }, [selectedAddProduct, selectedAddVariation, variationLabel]);

  const addPledgeAmountPreview = React.useMemo(() => {
    const v = selectedAddVariation?.price_num;
    const p = selectedAddProduct?.price_num;
    const n = Number(v ?? p ?? 0);
    return Number.isFinite(n) ? n : 0;
  }, [selectedAddProduct, selectedAddVariation]);

  const missingDialogRequiresVariation = React.useMemo(() => {
    if (!missingItemsOpen || !selectedAddProduct) return false;
    const totalVariations = Number(selectedAddProduct?.total_variations || 0);
    const linkedVariationIds = Array.isArray(selectedAddProduct?.linked_variation_ids)
      ? selectedAddProduct.linked_variation_ids
      : [];
    return totalVariations > 0 && linkedVariationIds.length > 0;
  }, [missingItemsOpen, selectedAddProduct]);

  const handleCreateInventoryItem = async () => {
    if (!selectedAddProduct?.id) {
      setAddItemError('Select a product.');
      return;
    }
    if (!addCategoryId) {
      setAddItemError('Select category.');
      return;
    }
    const qty = parseInt(String(addQuantity ?? '').trim(), 10);
    if (!Number.isInteger(qty) || qty < 0) {
      setAddItemError('Quantity must be a non-negative whole number.');
      return;
    }
    try {
      setAddItemSaving(true);
      setAddItemError('');
      setAddItemSuccess('');
      const created = await post('/ldb/kotilinga-inventory/items', {
        product_id: selectedAddProduct.id,
        variation_id: selectedAddVariation?.id ?? 0,
        category_id: Number(addCategoryId),
        quantity: qty,
      });
      await refreshItems();

      const createdItem = created?.data || null;
      const createdProductId = Number(createdItem?.product_id || selectedAddProduct?.id || 0);
      const createdVariationId = Number(createdItem?.variation_id || selectedAddVariation?.id || 0);

      let matchingCount = 0;
      if (createdProductId > 0) {
        try {
          const matchRes = await get('/ldb/kotilinga-inventory/matching-pledges', {
            product_id: String(createdProductId),
            variation_id: String(createdVariationId > 0 ? createdVariationId : 0),
          });
          matchingCount = Array.isArray(matchRes?.data) ? matchRes.data.length : 0;
        } catch {
          matchingCount = 0;
        }
      }

      if (matchingCount > 0 && createdItem) {
        setSelectedAddProduct(null);
        setSelectedAddVariation(null);
        setAddProductInput('');
        setAddProductOptions([]);
        setAddVariationOptions([]);
        setAddCategoryId('');
        setAddQuantity('0');
        setAddItemOpen(false);
        if (missingItemsOpen) setMissingItemsOpen(false);
        handleEdit({ ...createdItem, sponsors: [] });
        setSaveSuccess('Inventory item added. Review and save item details.');
        setTimeout(() => setSaveSuccess(''), 4000);
        return;
      }

      setAddItemSuccess('Inventory item added successfully. No unlinked pledges found.');
      if (missingItemsOpen) {
        try {
          const res = await get('/ldb/kotilinga-inventory/products-not-in-inventory');
          setMissingProducts(Array.isArray(res?.data) ? res.data : []);
        } catch {
          // keep existing list
        }
        setSelectedAddProduct(null);
        setSelectedAddVariation(null);
        setAddVariationOptions([]);
        setAddCategoryId('');
        setAddQuantity('0');
      }
      if (created?.data) {
        // Keep add dialog open so user can close after confirmation when no unlinked pledges exist.
      }
    } catch (e) {
      setAddItemError(e?.message || 'Failed to add inventory item');
    } finally {
      setAddItemSaving(false);
    }
  };

  const formatDevoteeName = (c) => {
    if (!c) return '';
    const first = c.first_name || c.billing_first_name || '';
    const last = c.last_name || c.billing_last_name || '';
    return `${first} ${last}`.trim() || c.email || '(No name)';
  };

  const openAddSponsor = (row, e) => {
    setAddSponsorManualName('');
    e?.stopPropagation?.();
    setAddSponsorItem(row);
    setDevoteeInput('');
    setDevoteeOptions([]);
    setSelectedDevotee(null);
    setPledges([]);
    setSelectedPledge(null);
    setAddSponsorError('');
    setAddSponsorEngraveName('');
    setAddSponsorOpen(true);
  };

  const openBulkAddSponsors = async (row, e) => {
    e?.stopPropagation?.();
    setBulkAddItem(row);
    setBulkAddOpen(true);
    setBulkPledges([]);
    setBulkSelectedPledgeIds([]);
    setBulkPledgesError('');

    const productId = Number(row?.product_id || 0);
    const variationId = Number(row?.variation_id || 0);
    if (!productId) {
      setBulkPledgesError('Link this inventory item to a product first, then add sponsors from matching pledges.');
      return;
    }

    try {
      setBulkPledgesLoading(true);
      const res = await get('/ldb/kotilinga-inventory/matching-pledges', {
        product_id: String(productId),
        variation_id: String(variationId > 0 ? variationId : 0),
      });
      setBulkPledges(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setBulkPledgesError(err?.message || 'Failed to load matching pledges');
    } finally {
      setBulkPledgesLoading(false);
    }
  };

  const handleBulkToggle = (pledgeId, checked) => {
    const key = String(pledgeId);
    setBulkSelectedPledgeIds((prev) => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      return prev.filter((id) => id !== key);
    });
  };

  const handleBulkAddSponsors = async () => {
    if (!bulkAddItem || bulkSelectedPledgeIds.length === 0) return;
    try {
      setBulkSaving(true);
      setBulkPledgesError('');

      const existing = (bulkAddItem.sponsors || []).map((s) => ({
        sponsor_name: s.sponsor_name ?? '',
        contact_id: s.contact_id ?? '',
        pledge_id: s.pledge_id ?? '',
        engrave_name: s.engrave_name ?? '',
      }));
      const existingPledgeIds = new Set(
        existing
          .map((s) => String(s.pledge_id ?? '').trim())
          .filter((id) => /^\d+$/.test(id))
      );

      const selectedRows = bulkPledges.filter((p) => bulkSelectedPledgeIds.includes(String(p.pledge_id)));
      const newSponsors = selectedRows
        .filter((p) => !existingPledgeIds.has(String(p.pledge_id)))
        .map((p) => ({
          sponsor_name: p.sponsor_name || 'Unknown sponsor',
          contact_id: p.contact_id != null ? String(p.contact_id) : '',
          pledge_id: String(p.pledge_id),
          engrave_name: '',
        }));

      if (newSponsors.length === 0) {
        setBulkPledgesError('All selected pledges are already linked to this item.');
        return;
      }

      await put(`/ldb/kotilinga-inventory/items/${bulkAddItem.inventory_id}/sponsors`, {
        sponsors: [...existing, ...newSponsors],
      });
      await refreshItems();
      setBulkAddOpen(false);
      setBulkAddItem(null);
      setBulkPledges([]);
      setBulkSelectedPledgeIds([]);
    } catch (err) {
      setBulkPledgesError(err?.message || 'Failed to add sponsors');
    } finally {
      setBulkSaving(false);
    }
  };

  const handleUnlinkSponsor = async (row, sponsorIndex) => {
    const sponsors = row.sponsors || [];
    if (sponsorIndex < 0 || sponsorIndex >= sponsors.length) return;
    const key = `${row.inventory_id}-${sponsorIndex}`;
    setUnlinkingSponsorKey(key);
    try {
      const updated = sponsors
        .filter((_, i) => i !== sponsorIndex)
        .map((s) => ({
          sponsor_name: s.sponsor_name ?? '',
          contact_id: s.contact_id ?? '',
          pledge_id: s.pledge_id ?? '',
          engrave_name: s.engrave_name ?? '',
        }));
      await put(`/ldb/kotilinga-inventory/items/${row.inventory_id}/sponsors`, { sponsors: updated });
      const params = { page: page.toString(), per_page: perPage.toString() };
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedQ) params.q = debouncedQ;
      const res = await get('/ldb/kotilinga-inventory/items', params);
      setData(Array.isArray(res?.data) ? res.data : []);
      setTotal(res?.meta?.total ?? 0);
      setTotalPages(res?.meta?.total_pages ?? 0);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to unlink sponsor');
      setTimeout(() => setError(''), 5000);
    } finally {
      setUnlinkingSponsorKey('');
    }
  };

  React.useEffect(() => {
    if (!addSponsorOpen || devoteeInput.trim().length < 2) {
      setDevoteeOptions([]);
      return;
    }
    let cancelled = false;
    setDevoteeLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await get('/ldb/customers', { q: devoteeInput.trim(), page: 1, per_page: 15 });
        const list = Array.isArray(res?.data) ? res.data : [];
        if (!cancelled) setDevoteeOptions(list);
      } catch (e) {
        if (!cancelled) setDevoteeOptions([]);
      } finally {
        if (!cancelled) setDevoteeLoading(false);
      }
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [addSponsorOpen, devoteeInput]);

  const handleAddSponsorSave = async () => {
    const nameManual = addSponsorManualName.trim();
    const nameFromDevotee = selectedDevotee ? formatDevoteeName(selectedDevotee) : '';
    const sponsorName = nameManual || nameFromDevotee;
    if (!addSponsorItem || !sponsorName) {
      setAddSponsorError('Enter sponsor name or select a devotee.');
      return;
    }
    setAddSponsorError('');
    try {
      setAddingSponsor(true);
      const current = (addSponsorItem.sponsors || []).map((s) => ({
        sponsor_name: s.sponsor_name ?? '',
        contact_id: s.contact_id ?? '',
        pledge_id: s.pledge_id ?? '',
        engrave_name: s.engrave_name ?? '',
      }));
      const newEntry = {
        sponsor_name: sponsorName,
        contact_id: selectedDevotee ? String(selectedDevotee.id) : '',
        pledge_id: '',
        engrave_name: addSponsorEngraveName.trim() || '',
      };
      const updated = [...current, newEntry];
      await put(`/ldb/kotilinga-inventory/items/${addSponsorItem.inventory_id}/sponsors`, { sponsors: updated });
      const params = { page: page.toString(), per_page: perPage.toString() };
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedQ) params.q = debouncedQ;
      const res = await get('/ldb/kotilinga-inventory/items', params);
      setData(Array.isArray(res?.data) ? res.data : []);
      setTotal(res?.meta?.total ?? 0);
      setTotalPages(res?.meta?.total_pages ?? 0);
      setAddSponsorOpen(false);
      setAddSponsorItem(null);
      setSelectedDevotee(null);
      setAddSponsorManualName('');
      setAddSponsorEngraveName('');
    } catch (e) {
      console.error(e);
      const msg = e?.message || e?.response?.data?.error || 'Failed to add sponsor.';
      setAddSponsorError(msg);
    } finally {
      setAddingSponsor(false);
    }
  };

  const handleCloseEdit = () => {
    if (!saving) {
      setEditOpen(false);
      setEditingItem(null);
      setEditProductInput('');
      setEditProductOptions([]);
      setSelectedEditProduct(null);
      setEditVariationOptions([]);
      setSaveError('');
      setSaveSuccess('');
      setEditUnlinkedError('');
      setEditUnlinkedRows([]);
      setEditUnlinkedSelectedIds([]);
    }
  };

  const openPaymentsDialog = async (sponsor) => {
    const productId = Number(sponsor?.pledge_product_id || 0);
    const variationIdRaw = sponsor?.pledge_variation_id;
    const customerId = sponsor?.pledge_customer_id != null ? Number(sponsor.pledge_customer_id) : null;
    const billingEmail = String(sponsor?.pledge_billing_email || '').trim().toLowerCase();
    if (!productId || (!customerId && !billingEmail)) return;

    setPaymentsOpen(true);
    setPaymentsLoading(true);
    setPaymentsError('');
    setPaymentOrders([]);
    setPaymentContext({
      sponsor_name: sponsor?.sponsor_name || 'Sponsor',
      pledge_id: sponsor?.pledge_id || '',
      product_id: productId,
      variation_id: variationIdRaw ?? null,
    });
    try {
      const params = {
        product_id: String(productId),
      };
      const variationId = variationIdRaw == null ? null : Number(variationIdRaw);
      if (variationId != null && !Number.isNaN(variationId)) params.variation_id = String(variationId);
      if (customerId && !Number.isNaN(customerId)) params.customer_id = String(customerId);
      else if (billingEmail) params.billing_email = billingEmail;
      const res = await get('/ldb/orders/pledge-payments', params);
      setPaymentOrders(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setPaymentsError(e?.message || 'Failed to load payment orders');
    } finally {
      setPaymentsLoading(false);
    }
  };

  const sponsorSummaryColumns = React.useMemo(() => {
    const rows = sponsorSummary || [];
    const midpoint = Math.ceil(rows.length / 2);
    return [rows.slice(0, midpoint), rows.slice(midpoint)];
  }, [sponsorSummary]);
  const sponsorSummaryTotals = React.useMemo(
    () => (sponsorSummary || []).reduce((sum, row) => {
      sum.sponsors += Number(row?.sponsors_count || 0) || 0;
      sum.pledged += Number(row?.pledge_amount_total || 0) || 0;
      sum.paid += Number(row?.paid_amount_total || 0) || 0;
      sum.balance += Number(row?.balance_amount_total || 0) || 0;
      return sum;
    }, { sponsors: 0, pledged: 0, paid: 0, balance: 0 }),
    [sponsorSummary]
  );
  const inventorySummaryMetrics = React.useMemo(() => {
    return {
      inventoryItems: Number(total || 0),
      uniqueItems: Number(total || 0),
    };
  }, [total]);
  const syncHasSelection = React.useMemo(() => {
    const addCount = Object.values(syncSelectedAdds || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    const removeCount = Object.values(syncSelectedRemoves || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
    return addCount + removeCount > 0;
  }, [syncSelectedAdds, syncSelectedRemoves]);

  const handleDownloadPdf = async () => {
    try {
      setExportingPdf(true);
      setError('');
      const params = {};
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (debouncedQ) params.q = debouncedQ;
      const res = await get('/ldb/kotilinga-inventory/export', params);
      const rows = Array.isArray(res?.rows) ? res.rows : [];
      const summary = res?.summary || {};
      if (rows.length === 0) {
        setError('No inventory rows available for export');
        return;
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const formatPdfCurrency = (value) => formatCurrency(Number(value || 0));
      const formatPdfDate = (value) => (value ? new Date(value).toLocaleDateString('en-US') : '—');
      const generatedAt = new Date().toLocaleString('en-US');
      const filters = [
        `Category: ${categoryFilter === 'all' ? 'All' : (categories.find((c) => String(c.category_id) === String(categoryFilter))?.category_name || categoryFilter)}`,
        `Status: ${statusFilter === 'all' ? 'All' : (statuses.find((s) => String(s.status_id) === String(statusFilter))?.status_name || statusFilter)}`,
        `Search: ${debouncedQ || 'All'}`
      ].join(' | ');

      doc.setFontSize(15);
      doc.text('Pledge Inventory Export', 40, 28);
      doc.setFontSize(9);
      doc.text(`Generated: ${generatedAt}`, 40, 44);
      doc.text(filters, 40, 58);

      autoTable(doc, {
        startY: 72,
        head: [['Metric', 'Value']],
        body: [
          ['Rows', Number(summary.row_count || 0).toLocaleString()],
          ['Unique Items', Number(summary.item_count || 0).toLocaleString()],
          ['Groups', Number(summary.group_count || 0).toLocaleString()],
          ['Rows With Pledges', Number(summary.pledged_rows || 0).toLocaleString()],
          ['Pledge Amount', formatPdfCurrency(summary.pledge_amount_total || 0)],
          ['Paid', formatPdfCurrency(summary.paid_amount_total || 0)],
          ['Balance', formatPdfCurrency(summary.balance_amount_total || 0)],
        ],
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [26, 35, 126] },
        margin: { left: 40, right: 40 },
        tableWidth: 280,
      });

      const detailStartY = (doc.lastAutoTable?.finalY || 72) + 18;
      autoTable(doc, {
        startY: detailStartY,
        head: [[
          'Item #',
          'Product Category',
          'Product Name',
          'Variation Name',
          'Sponsor Name',
          'Engrave Name',
          'Pledge Date',
          'Pledge ID',
          'Amount',
          'Paid',
          'Balance'
        ]],
        body: rows.map((row) => [
          row.item_no || '—',
          row.product_category || '—',
          row.product_name || '—',
          row.variation_name || '—',
          row.sponsor_name || '—',
          row.engrave_name || '—',
          formatPdfDate(row.pledge_date),
          row.pledge_id || '—',
          formatPdfCurrency(row.pledge_amount),
          formatPdfCurrency(row.paid_amount),
          formatPdfCurrency(row.balance_amount),
        ]),
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 4, overflow: 'linebreak' },
        headStyles: { fillColor: [26, 35, 126] },
        margin: { left: 40, right: 40 },
        columnStyles: {
          0: { cellWidth: 42 },
          1: { cellWidth: 104 },
          2: { cellWidth: 96 },
          3: { cellWidth: 82 },
          4: { cellWidth: 92 },
          5: { cellWidth: 92 },
          6: { cellWidth: 58 },
          7: { cellWidth: 50 },
          8: { cellWidth: 56, halign: 'right' },
          9: { cellWidth: 56, halign: 'right' },
          10: { cellWidth: 56, halign: 'right' },
        },
      });

      const fileDate = new Date().toISOString().slice(0, 10);
      doc.save(`kotilinga-inventory-export-${fileDate}.pdf`);
    } catch (e) {
      setError(e?.message || 'Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <PageLayout contentMaxWidth="xl" showHeaderImage={false} showOrgHeader>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate('/manage')} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
              Pledge Inventory
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Pledge-based slots (qty, status, links). Open sponsors via the people icon.
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleDownloadPdf}
            disabled={exportingPdf}
          >
            {exportingPdf ? 'Preparing PDF...' : 'Download PDF'}
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddItemDialog}>
            Add Inventory Item
          </Button>
          <Button variant="outlined" onClick={openMissingItemsDialog}>
            Missing Inventory Items
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Sponsors Summary by Category
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' },
            gap: 1.5,
            mb: 2,
          }}
        >
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Inventory Items</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{inventorySummaryMetrics.inventoryItems.toLocaleString()}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Unique Items</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{inventorySummaryMetrics.uniqueItems.toLocaleString()}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Sponsors</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{sponsorSummaryTotals.sponsors.toLocaleString()}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Pledged</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatSummaryCurrency(sponsorSummaryTotals.pledged)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Paid</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatSummaryCurrency(sponsorSummaryTotals.paid)}</Typography>
          </Paper>
          <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: '#f5f5f5' }}>
            <Typography variant="caption" color="text.secondary">Balance</Typography>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatSummaryCurrency(sponsorSummaryTotals.balance)}</Typography>
          </Paper>
        </Box>
        {sponsorSummary.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No sponsor summary available.
          </Typography>
        ) : (
          <Box
            sx={{
              mb: 2.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                columnGap: { xs: 0, md: 2 },
              }}
            >
              {sponsorSummaryColumns.map((columnRows, colIdx) => (
                <Box
                  key={`summary-col-${colIdx}`}
                  sx={{
                    px: { xs: 0, md: 0.5 },
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700, pr: 2 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: 2 }}>Sponsors</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: 2 }}>Pledged</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: 2 }}>Paid</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap', pl: 2 }}>Balance</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {columnRows.map((row) => (
                        <TableRow key={row.category_id}>
                          <TableCell sx={{ pr: 2 }}>{row.category_name || '—'}</TableCell>
                          <TableCell align="right" sx={{ pl: 2 }}>{Number(row.sponsors_count || 0).toLocaleString()}</TableCell>
                          <TableCell align="right" sx={{ pl: 2 }}>{formatSummaryCurrency(row.pledge_amount_total || 0)}</TableCell>
                          <TableCell align="right" sx={{ pl: 2 }}>{formatSummaryCurrency(row.paid_amount_total || 0)}</TableCell>
                          <TableCell align="right" sx={{ pl: 2 }}>{formatSummaryCurrency(row.balance_amount_total || 0)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}
            </Box>
          </Box>
        )}

      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, mb: 3, border: '1px solid', borderColor: 'divider' }}>
        <Box
          sx={{
            display: 'grid',
            gap: 2.5,
            alignItems: 'center',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(260px, 2fr) minmax(220px, 1.4fr)',
              md: 'minmax(360px, 3fr) minmax(260px, 1.8fr) minmax(180px, 1.1fr) auto',
            },
          }}
        >
          <Box>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Product, donor, engrave..."
              helperText={searchQ.trim().length > 0 && searchQ.trim().length < 2 ? 'Min 2 characters' : ''}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="cat-filter" shrink>Category</InputLabel>
              <Select
                labelId="cat-filter"
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                label="Category"
                notched
              >
                <MenuItem value="all">All</MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.category_id} value={String(c.category_id)}>{c.category_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter" shrink>Status</InputLabel>
              <Select
                labelId="status-filter"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                label="Status"
                notched
              >
                <MenuItem value="all">All</MenuItem>
                {statuses.map((s) => (
                  <MenuItem key={s.status_id} value={String(s.status_id)}>{s.status_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', pt: { xs: 0, md: 0.5 } }}>
              {(categoryFilter !== 'all' || statusFilter !== 'all' || debouncedQ) && (
                <Chip
                  label="Clear filters"
                  size="small"
                  onClick={() => {
                    setCategoryFilter('all');
                    setStatusFilter('all');
                    setSearchQ('');
                    setDebouncedQ('');
                    setPage(1);
                  }}
                />
              )}
              <Typography variant="body2" color="text.secondary">
                Total: {total} items
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages || 1} — {data.length} of {total} items
              </Typography>
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>

            <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    minWidth: 1120,
                    display: 'grid',
                    gridTemplateColumns: 'minmax(100px, 0.85fr) minmax(130px, 1fr) minmax(140px, 1fr) minmax(150px, 1.1fr) minmax(150px, 1.1fr) 56px 64px 72px 100px 150px',
                    bgcolor: 'action.hover',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    alignItems: 'center',
                  }}
                >
                <Box sx={{ px: 2, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Category</Typography></Box>
                <Box sx={{ px: 2, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Item Name</Typography></Box>
                <Box sx={{ px: 2, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Linked Product Category</Typography></Box>
                <Box sx={{ px: 2, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Linked Product</Typography></Box>
                <Box sx={{ px: 2, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Linked Variation</Typography></Box>
                <Box sx={{ px: 1, py: 1.75, textAlign: 'right' }}><Typography variant="caption" fontWeight={700}>Qty</Typography></Box>
                <Box sx={{ px: 1, py: 1.75, textAlign: 'right' }}><Typography variant="caption" fontWeight={700} title="Linked sponsor rows">Linked</Typography></Box>
                <Box sx={{ px: 1, py: 1.75 }}><Typography variant="caption" fontWeight={700}>Status</Typography></Box>
                <Box sx={{ px: 1, py: 1.75, textAlign: 'center' }}><Typography variant="caption" fontWeight={700}>Sponsors</Typography></Box>
                <Box sx={{ px: 2, py: 1.75, display: 'flex', justifyContent: 'center' }}><Typography variant="caption" fontWeight={700}>Action</Typography></Box>
              </Box>
              {data.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No items found</Typography>
                </Box>
              ) : (
                data.map((row, idx) => {
                  const linkedCount = (row.sponsors || []).length;
                  const uniqueCount = consolidateUniqueSponsors(row).length;
                  return (
                  <Box
                    key={row.inventory_id}
                    sx={{
                    minWidth: 1120,
                      display: 'grid',
                      gridTemplateColumns: 'minmax(100px, 0.85fr) minmax(130px, 1fr) minmax(140px, 1fr) minmax(150px, 1.1fr) minmax(150px, 1.1fr) 56px 64px 72px 100px 150px',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
                      alignItems: 'stretch',
                    }}
                  >
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.category_name || ''}>
                        {row.category_name || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.product_name || ''}>
                        {row.product_name || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.linked_product_category_names || ''}>
                        {row.linked_product_category_names || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.linked_product_name || ''}>
                        {row.linked_product_name || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.linked_variation_name || ''}>
                        {row.linked_variation_name || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.quantity != null ? Number(row.quantity).toLocaleString() : '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography variant="body2" color="text.secondary" title="Sponsor links (rows)">
                        {linkedCount}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 1.75, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.status_name || ''}>
                        {row.status_name || '—'}
                      </Typography>
                    </Box>
                    <Box sx={{ px: 1, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 28, textAlign: 'center' }} title="Unique sponsor names">
                        {uniqueCount || '—'}
                      </Typography>
                      <Tooltip title={uniqueCount ? 'View unique sponsors' : 'No sponsors linked'}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); if (linkedCount) setSponsorsDialogRow(row); }}
                            disabled={!linkedCount}
                            aria-label="View sponsors"
                            color="primary"
                          >
                            <PeopleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                    <Box sx={{ px: 2, py: 1.75, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.25 }}>
                      <Tooltip title="Add sponsor">
                        <IconButton size="small" onClick={(e) => openAddSponsor(row, e)} sx={{ color: 'primary.main' }} aria-label="Add sponsor">
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit item">
                        <IconButton size="small" onClick={() => handleEdit(row)} sx={{ color: 'primary.main' }} aria-label="Edit item">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  );
                })
              )}
            </Box>

            <Box sx={{ px: 2, py: 1.5, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'action.hover', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages || 1} — {data.length} of {total} items
              </Typography>
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={!!sponsorsDialogRow} onClose={() => setSponsorsDialogRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>Sponsors (unique names)</Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={sponsorsDialogRow?.product_name}>
              {sponsorsDialogRow?.product_name || 'Item'} · {(sponsorsDialogRow?.sponsors || []).length} link(s) → {consolidateUniqueSponsors(sponsorsDialogRow || {}).length} unique
            </Typography>
          </Box>
          <IconButton onClick={() => setSponsorsDialogRow(null)} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {sponsorsDialogRow && consolidateUniqueSponsors(sponsorsDialogRow).length === 0 ? (
            <Typography variant="body2" color="text.secondary">No sponsors linked.</Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Links</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sponsorsDialogRow && consolidateUniqueSponsors(sponsorsDialogRow).map((u) => (
                  <TableRow key={u.displayName}>
                    <TableCell>
                      {u.contact_id ? (
                        <Link to={`/devotees/${encodeURIComponent(u.contact_id)}`} style={{ color: 'inherit', fontWeight: 500 }}>
                          {u.displayName}
                        </Link>
                      ) : (
                        u.displayName
                      )}
                    </TableCell>
                    <TableCell align="right">{u.pledgeCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Same person linked multiple times (e.g. multiple orders) is shown once; Links = number of sponsor rows for that name.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSponsorsDialogRow(null)}>Close</Button>
          {sponsorsDialogRow && (
            <Button variant="outlined" onClick={() => { handleEdit(sponsorsDialogRow); setSponsorsDialogRow(null); }}>
              Edit item (unlink / add sponsors)
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={syncOpen} onClose={() => { if (!syncSaving) setSyncOpen(false); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Review Sponsor Sync</Typography>
          <Typography variant="body2" color="text.secondary">
            New pledges and voided linked pledges for manual review.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {syncError && <Typography color="error" sx={{ mb: 1.5 }}>{syncError}</Typography>}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`Items with new: ${syncMeta.items_with_new || 0}`} size="small" />
            <Chip label={`New pledges: ${syncMeta.new_pledges_total || 0}`} size="small" />
            <Chip label={`Items with voided: ${syncMeta.items_with_voided || 0}`} size="small" />
            <Chip label={`Voided links: ${syncMeta.voided_total || 0}`} size="small" />
          </Box>
          {syncLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : syncGroups.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No linked items found.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {syncGroups.filter((g) => (
                  (g.new_pledges || []).length > 0 ||
                  (g.voided_links || []).length > 0 ||
                  (g.linked_issues || []).length > 0
                )).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No new pledges, voided links, or linked pledge issues found.
                </Typography>
              ) : syncGroups
                .filter((g) => (
                  (g.new_pledges || []).length > 0 ||
                  (g.voided_links || []).length > 0 ||
                  (g.linked_issues || []).length > 0
                ))
                .map((g) => (
                  <Box key={g.inventory_id} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      #{g.inventory_id} · {[
                        g.linked_product_name || g.item_name || '',
                        g.linked_variation_name || '',
                      ].filter(Boolean).join(' - ') || '—'}
                    </Typography>
                    {(g.linked_issues || []).length > 0 && (
                      <Box sx={{ mb: 1.25 }}>
                        {(g.linked_issues || []).map((iss) => (
                          <Typography key={`${g.inventory_id}-issue-${iss.sponsor_row_id || iss.message}`} variant="caption" color="error" sx={{ display: 'block' }}>
                            Issue: {iss.message}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                      {(g.new_pledges || []).length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            New pledges to link
                          </Typography>
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            {(g.new_pledges || []).map((p) => (
                              <Box key={`${g.inventory_id}-new-${p.pledge_id}`} sx={{ px: 1.25, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={Boolean((syncSelectedAdds[String(g.inventory_id)] || []).includes(String(p.pledge_id)))}
                                  onChange={(e) => toggleSyncAdd(g.inventory_id, p.pledge_id, e.target.checked)}
                                />
                                <Typography variant="body2">
                                  Pledge #{p.pledge_id} · {p.sponsor_name || 'Unknown sponsor'} · {p.pledge_date ? new Date(p.pledge_date).toLocaleDateString() : '—'}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                      {(g.voided_links || []).length > 0 && (
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Voided linked pledges (select to unlink)
                          </Typography>
                          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            {(g.voided_links || []).map((v) => (
                              <Box key={`${g.inventory_id}-void-${v.sponsor_row_id}`} sx={{ px: 1.25, py: 0.9, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' }, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  size="small"
                                  checked={Boolean((syncSelectedRemoves[String(g.inventory_id)] || []).includes(String(v.sponsor_row_id)))}
                                  onChange={(e) => toggleSyncRemove(g.inventory_id, v.sponsor_row_id, e.target.checked)}
                                />
                                <Typography variant="body2">
                                  Pledge #{v.pledge_id} · {v.sponsor_name || 'Unknown sponsor'} · <Box component="span" sx={{ color: '#f57c00', fontWeight: 700 }}>{v.reason}</Box>
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setSyncOpen(false)} disabled={syncSaving}>Close</Button>
          <Button
            variant="contained"
            onClick={applySyncSelections}
            disabled={syncSaving || !syncHasSelection}
          >
            {syncSaving ? <CircularProgress size={20} /> : 'Apply Selected'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exceptionOpen} onClose={() => { if (!exceptionLoading) setExceptionOpen(false); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Inventory Exception Report</Typography>
          <Typography variant="body2" color="text.secondary">
            Findings across all inventory items.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {exceptionError && <Alert severity="error" sx={{ mb: 1.5 }}>{exceptionError}</Alert>}
          {exceptionLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                <Chip size="small" label={`Items: ${exceptionMeta.items_with_alerts || 0}`} />
                <Chip size="small" label={`Total findings: ${exceptionMeta.total_alerts || 0}`} />
                <Chip size="small" label={`Missing pledges: ${exceptionMeta.missing_orders_total || 0}`} />
                <Chip size="small" label={`Unlinked pledges: ${exceptionMeta.unlinked_pledges_total || 0}`} />
              </Box>
              {exceptionRows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No exceptions found.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Missing Pledges
                    </Typography>
                    {exceptionRows.filter((r) => Number(r?.missing_orders_count || 0) > 0).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No missing pledges found.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Inventory #</TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell>Linked Product</TableCell>
                              <TableCell>Linked Variation</TableCell>
                              <TableCell align="right">Missing</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {exceptionRows
                              .filter((r) => Number(r?.missing_orders_count || 0) > 0)
                              .map((r) => (
                                <TableRow key={`exc-missing-${r.inventory_id}`}>
                                  <TableCell>{r.inventory_id}</TableCell>
                                  <TableCell>{r.item_name || '—'}</TableCell>
                                  <TableCell>{r.category_name || '—'}</TableCell>
                                  <TableCell>{r.linked_product_name || '—'}</TableCell>
                                  <TableCell>{r.linked_variation_name || '—'}</TableCell>
                                  <TableCell align="right">{r.missing_orders_count || 0}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      onClick={async () => {
                                        setExceptionOpen(false);
                                        await openIssuesDialog({
                                          inventory_id: r.inventory_id,
                                          product_name: r.item_name || '',
                                        });
                                      }}
                                    >
                                      View
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 700 }}>
                      Unlinked Pledges
                    </Typography>
                    {exceptionRows.filter((r) => Number(r?.unlinked_pledges_count || 0) > 0).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">No missing links found.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Inventory #</TableCell>
                              <TableCell>Item</TableCell>
                              <TableCell>Category</TableCell>
                              <TableCell>Linked Product</TableCell>
                              <TableCell>Linked Variation</TableCell>
                              <TableCell align="right">Unlinked</TableCell>
                              <TableCell align="right">Action</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {exceptionRows
                              .filter((r) => Number(r?.unlinked_pledges_count || 0) > 0)
                              .map((r) => (
                                <TableRow key={`exc-unlinked-${r.inventory_id}`}>
                                  <TableCell>{r.inventory_id}</TableCell>
                                  <TableCell>{r.item_name || '—'}</TableCell>
                                  <TableCell>{r.category_name || '—'}</TableCell>
                                  <TableCell>{r.linked_product_name || '—'}</TableCell>
                                  <TableCell>{r.linked_variation_name || '—'}</TableCell>
                                  <TableCell align="right">{r.unlinked_pledges_count || 0}</TableCell>
                                  <TableCell align="right">
                                    <Button
                                      size="small"
                                      onClick={async () => {
                                        setExceptionOpen(false);
                                        await openIssuesDialog({
                                          inventory_id: r.inventory_id,
                                          product_name: r.item_name || '',
                                        });
                                      }}
                                    >
                                      View
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExceptionOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={issuesOpen} onClose={() => { if (!issuesSaving) setIssuesOpen(false); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Inventory Findings</Typography>
          <Typography variant="body2" color="text.secondary">
            {issuesData
              ? `#${issuesData.inventory_id} · ${issuesData.item_name || 'Item'}`
              : (issuesItem ? `#${issuesItem.inventory_id} · ${issuesItem.product_name || 'Item'}` : 'Review issues')}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {issuesError && <Alert severity="error" sx={{ mb: 1.5 }}>{issuesError}</Alert>}
          {issuesSuccess && <Alert severity="success" sx={{ mb: 1.5 }}>{issuesSuccess}</Alert>}
          {issuesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : !issuesData ? (
            <Typography variant="body2" color="text.secondary">No findings loaded.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Orders without pledge (eligible to convert to pledge-pending)
                </Typography>
                {(issuesData.missing_pledge_customers || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              indeterminate={
                                selectedMissingCustomerKeys.length > 0
                                && selectedMissingCustomerKeys.length < (issuesData.missing_pledge_customers || []).length
                              }
                              checked={
                                (issuesData.missing_pledge_customers || []).length > 0
                                && selectedMissingCustomerKeys.length === (issuesData.missing_pledge_customers || []).length
                              }
                              onChange={(e) => toggleAllMissingOrders(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>Order(s)</TableCell>
                          <TableCell>Sponsor</TableCell>
                          <TableCell>Date (Oldest)</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(issuesData.missing_pledge_customers || []).map((r) => (
                          <TableRow key={`missing-${r.group_key}`}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedMissingCustomerKeys.includes(String(r.group_key))}
                                onChange={(e) => toggleMissingCustomer(r.group_key, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {(r.order_ids || []).map((oid) => (
                                  <Button
                                    key={`missing-oid-${r.group_key}-${oid}`}
                                    size="small"
                                    sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                    onClick={() => openOrderEditDialog(oid)}
                                  >
                                    #{oid}
                                  </Button>
                                ))}
                              </Box>
                            </TableCell>
                            <TableCell>{r.sponsor_name || '—'}</TableCell>
                            <TableCell>{r.oldest_order_date ? new Date(r.oldest_order_date).toLocaleDateString('en-US') : '—'}</TableCell>
                            <TableCell align="right">{r.total_amount != null ? formatCurrency(r.total_amount) : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25 }}>
                  <Button
                    variant="outlined"
                    onClick={handleCreateMissingPledges}
                    disabled={issuesSaving || selectedMissingCustomerKeys.length === 0}
                  >
                    {issuesSaving ? 'Working...' : 'Create Selected Pledges'}
                  </Button>
                </Box>
              </Box>

              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Pledges found but not linked
                </Typography>
                {(issuesData.unlinked_pledges || []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">None.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              size="small"
                              indeterminate={
                                selectedUnlinkedPledgeIds.length > 0
                                && selectedUnlinkedPledgeIds.length < (issuesData.unlinked_pledges || []).length
                              }
                              checked={
                                (issuesData.unlinked_pledges || []).length > 0
                                && selectedUnlinkedPledgeIds.length === (issuesData.unlinked_pledges || []).length
                              }
                              onChange={(e) => toggleAllUnlinkedPledges(e.target.checked)}
                            />
                          </TableCell>
                          <TableCell>Pledge #</TableCell>
                          <TableCell>Sponsor</TableCell>
                          <TableCell>Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(issuesData.unlinked_pledges || []).map((r) => (
                          <TableRow key={`unlinked-${r.pledge_id}`}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedUnlinkedPledgeIds.includes(String(r.pledge_id))}
                                onChange={(e) => toggleUnlinkedPledge(r.pledge_id, e.target.checked)}
                              />
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                sx={{ p: 0, minWidth: 'auto', textTransform: 'none' }}
                                onClick={() => openOrderEditDialog(r.pledge_id)}
                              >
                                #{r.pledge_id}
                              </Button>
                            </TableCell>
                            <TableCell>{r.sponsor_name || '—'}</TableCell>
                            <TableCell>{r.pledge_date ? new Date(r.pledge_date).toLocaleDateString('en-US') : '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.25 }}>
                  <Button
                    variant="contained"
                    onClick={handleLinkUnlinkedPledges}
                    disabled={issuesSaving || selectedUnlinkedPledgeIds.length === 0}
                  >
                    {issuesSaving ? 'Working...' : 'Link Selected Pledges'}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuesOpen(false)} disabled={issuesSaving}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addItemOpen} onClose={closeAddItemDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Add Inventory Item</Typography>
              <Typography variant="body2" color="text.secondary">
                Select product/variation. Item details will be auto-filled.
              </Typography>
            </Box>
            <IconButton onClick={closeAddItemDialog} size="small" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {addItemError && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {addItemError}
            </Typography>
          )}
          {addItemSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mb: 1.5 }}>
              {addItemSuccess}
            </Typography>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Select Product</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                <Autocomplete
                  disablePortal
                  options={addProductOptions}
                  loading={addProductLoading}
                  inputValue={addProductInput}
                  onInputChange={(_, value) => setAddProductInput(value)}
                  value={selectedAddProduct}
                  onChange={(_, value) => {
                    setSelectedAddProduct(value || null);
                    setSelectedAddVariation(null);
                    setAddVariationOptions([]);
                    if (value?.id) void loadAddVariations(value.id);
                  }}
                  getOptionLabel={(p) => productLabel(p)}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Product"
                      placeholder="Type at least 2 characters to search..."
                    />
                  )}
                />
                <Autocomplete
                  disablePortal
                  options={addVariationOptions}
                  loading={addVariationLoading}
                  value={selectedAddVariation}
                  onChange={(_, value) => setSelectedAddVariation(value || null)}
                  getOptionLabel={(v) => variationLabel(v)}
                  isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      label="Variation (optional)"
                      placeholder={selectedAddProduct ? 'Select variation if applicable' : 'Select product first'}
                    />
                  )}
                  disabled={!selectedAddProduct}
                />
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={addCategoryId}
                    onChange={(e) => setAddCategoryId(e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="">Select category</MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c.category_id} value={String(c.category_id)}>{c.category_name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Auto-filled Values</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Item name"
                  value={addItemNamePreview || ''}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Pledge amount"
                  value={formatCurrency(addPledgeAmountPreview)}
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Quantity"
                  type="number"
                  value={addQuantity}
                  onChange={(e) => setAddQuantity(e.target.value)}
                  inputProps={{ min: 0, step: 1 }}
                />
                <TextField
                  fullWidth
                  size="small"
                  label="Status"
                  value="Available"
                  InputProps={{ readOnly: true }}
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeAddItemDialog} disabled={addItemSaving}>Cancel</Button>
          <Button onClick={handleCreateInventoryItem} variant="contained" disabled={addItemSaving || !selectedAddProduct || !addCategoryId}>
            {addItemSaving ? <CircularProgress size={20} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={missingItemsOpen} onClose={closeMissingItemsDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Missing Inventory Items</Typography>
              <Typography variant="body2" color="text.secondary">
                Kotilinga products not yet in inventory. Select a product, then variation (if any), category and quantity.
              </Typography>
            </Box>
            <IconButton onClick={closeMissingItemsDialog} size="small" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2.5 }}>
          {missingProductsError && (
            <Alert severity="error" sx={{ mb: 1.5 }}>{missingProductsError}</Alert>
          )}
          {addItemError && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{addItemError}</Typography>
          )}
          {addItemSuccess && (
            <Typography variant="body2" color="success.main" sx={{ mb: 1.5 }}>{addItemSuccess}</Typography>
          )}
          {missingProductsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : missingProducts.length === 0 && !selectedAddProduct ? (
            <Typography variant="body2" color="text.secondary">No missing products. All Kotilinga/Kadambavanam products are already in inventory.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.5 }}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="missing-inv-product-label" shrink>
                      Product
                    </InputLabel>
                    <Select
                      labelId="missing-inv-product-label"
                      value={selectedAddProduct ? String(selectedAddProduct.id) : ''}
                      onChange={(e) => {
                        const id = e.target.value;
                        const p = missingProducts.find((x) => String(x.product_id) === String(id));
                        if (p) {
                          setSelectedAddProduct({
                            id: p.product_id,
                            name: p.product_name,
                            linked_variation_ids: Array.isArray(p.linked_variation_ids) ? p.linked_variation_ids : [],
                            total_variations: Number(p.total_variations || 0),
                            missing_variations_count: Number(p.missing_variations_count || 0),
                          });
                          setSelectedAddVariation(null);
                          setAddVariationOptions([]);
                          void loadAddVariations(p.product_id, {
                            excludeVariationIds: Array.isArray(p.linked_variation_ids) ? p.linked_variation_ids : [],
                          });
                        }
                      }}
                      label="Product"
                      displayEmpty
                      notched
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography component="span" variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                              Choose product not yet in inventory
                            </Typography>
                          );
                        }
                        const p = missingProducts.find((x) => String(x.product_id) === String(selected));
                        return p?.product_name || `Product #${selected}`;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Choose product not yet in inventory</em>
                      </MenuItem>
                      {missingProducts.map((p) => (
                        <MenuItem key={p.product_id} value={String(p.product_id)}>
                          {p.product_name || `Product #${p.product_id}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {missingDialogRequiresVariation && (
                    <Alert severity="info">
                      Some variations of this product are already linked in inventory. Select one of the remaining variations below.
                    </Alert>
                  )}
                  <Autocomplete
                    disablePortal
                    options={addVariationOptions}
                    loading={addVariationLoading}
                    value={selectedAddVariation}
                    onChange={(_, value) => setSelectedAddVariation(value || null)}
                    getOptionLabel={(v) => variationLabel(v)}
                    isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        label="Variation (optional)"
                        placeholder={selectedAddProduct ? 'Select variation if applicable' : 'Select product first'}
                      />
                    )}
                    disabled={!selectedAddProduct}
                  />
                  <FormControl fullWidth size="small">
                    <InputLabel>Category</InputLabel>
                    <Select
                      value={addCategoryId}
                      onChange={(e) => setAddCategoryId(e.target.value)}
                      label="Category"
                    >
                      <MenuItem value="">Select category</MenuItem>
                      {categories.map((c) => (
                        <MenuItem key={c.category_id} value={String(c.category_id)}>{c.category_name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Auto-filled Values</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.5 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Item name"
                    value={addItemNamePreview || ''}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Pledge amount"
                    value={formatCurrency(addPledgeAmountPreview)}
                    InputProps={{ readOnly: true }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity"
                    type="number"
                    value={addQuantity}
                    onChange={(e) => setAddQuantity(e.target.value)}
                    inputProps={{ min: 0, step: 1 }}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Status"
                    value="Available"
                    InputProps={{ readOnly: true }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={closeMissingItemsDialog} disabled={addItemSaving}>Cancel</Button>
          <Button
            onClick={handleCreateInventoryItem}
            variant="contained"
            disabled={addItemSaving || !selectedAddProduct || !addCategoryId || (missingDialogRequiresVariation && !selectedAddVariation)}
          >
            {addItemSaving ? <CircularProgress size={20} /> : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={handleCloseEdit} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6">Edit inventory item</Typography>
              {saveSuccess && <Typography variant="body2" color="success.main">{saveSuccess}</Typography>}
              {saveError && <Typography variant="body2" color="error.main">{saveError}</Typography>}
            </Box>
            <IconButton onClick={handleCloseEdit} size="small" sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          {editingItem && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Item Details</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Inventory ID" value={editingItem.inventory_id ?? ''} InputProps={{ readOnly: true }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select value={editingItem.category_id ?? ''} onChange={(e) => setEditingItem({ ...editingItem, category_id: e.target.value })} label="Category">
                        <MenuItem value="">—</MenuItem>
                        {categories.map((c) => (
                          <MenuItem key={c.category_id} value={c.category_id}>{c.category_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" label="Item name" value={editingItem.product_name ?? ''} onChange={(e) => setEditingItem({ ...editingItem, product_name: e.target.value })} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Product no"
                      value={editingItem.product_no ?? ''}
                      onChange={(e) => setEditingItem({ ...editingItem, product_no: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="number" label="Quantity" value={editingItem.quantity} onChange={(e) => setEditingItem({ ...editingItem, quantity: e.target.value })} inputProps={{ min: 0 }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField fullWidth size="small" type="number" label="Pledge amount" value={editingItem.pledge_amount} onChange={(e) => setEditingItem({ ...editingItem, pledge_amount: e.target.value })} inputProps={{ min: 0, step: 0.01 }} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={editingItem.status ?? ''} onChange={(e) => setEditingItem({ ...editingItem, status: e.target.value })} label="Status">
                        <MenuItem value="">—</MenuItem>
                        {statuses.map((s) => (
                          <MenuItem key={s.status_id} value={s.status_id}>{s.status_name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Link Item to Product / Variation</Typography>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2,
                    width: '100%',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      sx={{ width: '100%' }}
                      style={{ width: '100%' }}
                      size="small"
                      options={editProductOptions}
                      loading={editProductLoading}
                      ListboxProps={{ style: { maxHeight: 420 } }}
                      inputValue={editProductInput}
                      onInputChange={(_, value) => setEditProductInput(value)}
                      value={selectedEditProduct}
                      onChange={(_, value) => {
                        setSelectedEditProduct(value || null);
                        setEditingItem({
                          ...editingItem,
                          product_id: value?.id ?? '',
                          variation_id: '',
                        });
                        setEditVariationOptions([]);
                        if (value?.id) void loadEditVariations(value.id);
                      }}
                      getOptionLabel={(p) => productLabel(p)}
                      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Product"
                          placeholder="Type 2+ characters to search and select product..."
                        />
                      )}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Autocomplete
                      sx={{ width: '100%' }}
                      style={{ width: '100%' }}
                      size="small"
                      options={editVariationOptions}
                      loading={editVariationLoading}
                      ListboxProps={{ style: { maxHeight: 420 } }}
                      value={editVariationOptions.find((v) => Number(v.id) === Number(editingItem.variation_id)) || null}
                      onChange={(_, value) => setEditingItem({ ...editingItem, variation_id: value?.id ?? '' })}
                      getOptionLabel={(v) => variationLabel(v)}
                      isOptionEqualToValue={(a, b) => Number(a?.id) === Number(b?.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          label="Variation (optional)"
                          placeholder={editingItem.product_id ? 'Select variation for selected product' : 'Select product first'}
                        />
                      )}
                      disabled={!editingItem.product_id}
                    />
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Add or remove sponsors from the list using <strong>Add sponsor</strong> (row actions) or the people icon dialog. Inventory is not tied to pledges.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : 'Save Item Details'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addSponsorOpen} onClose={() => { if (!addingSponsor) { setAddSponsorOpen(false); setAddSponsorError(''); setAddSponsorEngraveName(''); setAddSponsorManualName(''); } }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {`Add sponsor${addSponsorItem?.product_name ? ` — ${addSponsorItem.product_name}` : ''}`}
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Sponsors are stored on the inventory item only (no pledge required). Upfront payers and pledge-based donors are tracked the same way here.
          </Typography>
          {addSponsorError && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>{addSponsorError}</Typography>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Sponsor name"
                value={addSponsorManualName}
                onChange={(e) => setAddSponsorManualName(e.target.value)}
                placeholder="Required if no devotee selected"
                variant="outlined"
                size="medium"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                size="medium"
                inputValue={devoteeInput}
                onInputChange={(_, value) => setDevoteeInput(value)}
                value={selectedDevotee}
                onChange={(_, newValue) => { setSelectedDevotee(newValue); if (newValue && !addSponsorManualName.trim()) setAddSponsorManualName(''); }}
                options={devoteeOptions}
                getOptionLabel={(c) => (c ? `${formatDevoteeName(c)}${c.email ? ` · ${c.email}` : ''}` : '')}
                loading={devoteeLoading}
                ListboxProps={{ sx: { maxHeight: 320 } }}
                renderInput={(params) => (
                  <TextField {...params} fullWidth label="Link devotee (optional)" placeholder="Search 2+ characters…" />
                )}
                isOptionEqualToValue={(a, b) => a?.id === b?.id}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Engrave name (optional)"
                value={addSponsorEngraveName}
                onChange={(e) => setAddSponsorEngraveName(e.target.value)}
                variant="outlined"
                size="medium"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setAddSponsorOpen(false); setAddSponsorError(''); setAddSponsorEngraveName(''); setAddSponsorManualName(''); }} disabled={addingSponsor}>Cancel</Button>
          <Button onClick={handleAddSponsorSave} variant="contained" disabled={addingSponsor || (!addSponsorManualName.trim() && !selectedDevotee)}>
            {addingSponsor ? <CircularProgress size={20} /> : 'Add sponsor'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkAddOpen}
        onClose={() => { if (!bulkSaving) { setBulkAddOpen(false); setBulkAddItem(null); setBulkPledges([]); setBulkSelectedPledgeIds([]); setBulkPledgesError(''); } }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {`Add sponsors from pledges${bulkAddItem?.product_name ? ` - ${bulkAddItem.product_name}` : ''}`}
        </DialogTitle>
        <DialogContent dividers>
          {bulkPledgesError && (
            <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>
              {bulkPledgesError}
            </Typography>
          )}
          {bulkPledgesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : bulkPledges.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No matching pledges found for this linked product/variation.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={50}>Add</TableCell>
                    <TableCell>Pledge ID</TableCell>
                    <TableCell>Sponsor name</TableCell>
                    <TableCell>Pledge date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bulkPledges.map((p) => {
                    const pledgeId = String(p.pledge_id);
                    const linkedAlready = (bulkAddItem?.sponsors || []).some((s) => String(s?.pledge_id || '') === pledgeId);
                    const checked = bulkSelectedPledgeIds.includes(pledgeId);
                    return (
                      <TableRow key={pledgeId}>
                        <TableCell>
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={linkedAlready}
                            onChange={(e) => handleBulkToggle(pledgeId, e.target.checked)}
                          />
                        </TableCell>
                        <TableCell>{pledgeId}</TableCell>
                        <TableCell>
                          {p.sponsor_name || '—'}
                          {linkedAlready && (
                            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                              (already linked)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{p.pledge_date ? new Date(p.pledge_date).toLocaleDateString() : '—'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBulkAddOpen(false); setBulkAddItem(null); setBulkPledges([]); setBulkSelectedPledgeIds([]); setBulkPledgesError(''); }} disabled={bulkSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkAddSponsors}
            disabled={bulkSaving || bulkSelectedPledgeIds.length === 0}
          >
            {bulkSaving ? <CircularProgress size={20} /> : 'Add sponsors'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={paymentsOpen} onClose={() => setPaymentsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Payment Orders {paymentContext?.sponsor_name ? `- ${paymentContext.sponsor_name}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {paymentContext && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Pledge #{paymentContext.pledge_id || '—'} · Product {paymentContext.product_id}
              {paymentContext.variation_id != null ? ` · Variation ${paymentContext.variation_id}` : ''}
            </Typography>
          )}
          {paymentsError && <Typography color="error" sx={{ mb: 1.5 }}>{paymentsError}</Typography>}
          {paymentsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : paymentOrders.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No orders found.</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Order ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentOrders.map((o) => (
                    <TableRow key={o.id}>
                      <TableCell>{o.id}</TableCell>
                      <TableCell>{o.date_created_gmt ? new Date(o.date_created_gmt).toLocaleString() : '—'}</TableCell>
                      <TableCell align="right">{o.paid_amount != null ? `$${Number(o.paid_amount).toFixed(2)}` : '—'}</TableCell>
                      <TableCell>{o.status || '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </PageLayout>
  );
}
